import { colors, spacing } from "@procore/core-react";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { HEADER_BORDER } from "components/window/constants";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useDispatchHighlighted } from "contexts/highlighted";
import { selectDefaultVisibility, selectPreIsolationVisibility } from "features/render";
import { isolateSimilarInXray } from "utils/objectActions";

import { useGroupExpansion } from "../hooks/useGroupExpansion";
import { usePropertyFiltering } from "../hooks/usePropertyFiltering";
import { usePropertySelection } from "../hooks/usePropertySelection";
import { PropertiesTabProps } from "../types";
import { BulkActionsDropdown } from "./BulkActionsDropdown";
import { PropertyGroupList } from "./PropertyGroupList";

export function PropertiesTabContent({ object, searchTerm, isSearchActive, onEmptyStateChange }: PropertiesTabProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const dispatchHighlighted = useDispatchHighlighted();
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const preIsolationVisibility = useAppSelector(selectPreIsolationVisibility);
    const defaultVisibilityRef = useRef(defaultVisibility);
    defaultVisibilityRef.current = defaultVisibility;
    const preIsolationVisibilityRef = useRef(preIsolationVisibility);
    preIsolationVisibilityRef.current = preIsolationVisibility;
    const {
        state: { db },
    } = useExplorerGlobals(true);
    const mountedRef = useRef(false);

    // Create grouped properties for filtering
    const groupedProperties = useMemo(() => {
        if (!object) {
            return [];
        }

        const groups = [];

        const baseProps = object.base.filter(([, value]) => value);
        if (baseProps.length > 0) {
            groups.push({
                name: t("general"),
                properties: baseProps,
            });
        }

        Object.entries(object.grouped).forEach(([groupName, group]) => {
            const filteredProps = group.properties.filter(([, value]) => value);
            if (filteredProps.length > 0) {
                groups.push({
                    name: groupName,
                    properties: filteredProps,
                });
            }
        });

        return groups;
    }, [object, t]);

    const { filteredGroups, hasSearchResults } = usePropertyFiltering(groupedProperties, searchTerm);
    const { isPropertySelected, getGroupStats, togglePropertySelection, toggleGroupSelection, selectedProperties } =
        usePropertySelection(filteredGroups);

    const { isGroupExpanded, toggleGroupExpanded } = useGroupExpansion();

    // Calculate selection counts for bulk actions
    const selectedCount = selectedProperties.size;
    const totalCount = filteredGroups.reduce((sum, group) => sum + group.properties.length, 0);

    // Isolate similar objects based on selected properties
    useEffect(() => {
        if (mountedRef.current) {
            isolateSimilarInXray(selectedProperties, {
                db,
                dispatch,
                dispatchHighlighted,
                currentDefaultVisibility: preIsolationVisibilityRef.current ?? defaultVisibilityRef.current,
            });
        }
    }, [selectedProperties, db, dispatch, dispatchHighlighted]);

    const sortedGroups = useMemo(
        () => filteredGroups.toSorted((a, b) => a.name.localeCompare(b.name, "en", { sensitivity: "accent" })),
        [filteredGroups],
    );

    // Communicate search empty state to parent
    useEffect(() => {
        if (onEmptyStateChange && isSearchActive) {
            onEmptyStateChange(!hasSearchResults);
        }
    }, [hasSearchResults, isSearchActive, onEmptyStateChange]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
            }}
        >
            {selectedCount > 0 && (
                <div
                    style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        padding: spacing.md,
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                        borderBottom: HEADER_BORDER,
                        backgroundColor: colors.blue98,
                    }}
                >
                    <BulkActionsDropdown selectedCount={selectedCount} totalCount={totalCount} />
                </div>
            )}
            <div
                style={{
                    flex: 1,
                    padding: spacing.lg,
                    display: "flex",
                    flexDirection: "column",
                    gap: spacing.sm,
                    overflowY: "auto",
                }}
            >
                <PropertyGroupList
                    groups={sortedGroups}
                    searchTerm={searchTerm}
                    isSearchActive={isSearchActive}
                    isGroupExpanded={isGroupExpanded}
                    getGroupStats={getGroupStats}
                    isPropertySelected={isPropertySelected}
                    onToggleGroupExpansion={toggleGroupExpanded}
                    onToggleGroupSelection={toggleGroupSelection}
                    onTogglePropertySelection={togglePropertySelection}
                />
            </div>
        </div>
    );
}
